import * as React from 'react'
import { useSignupContext } from '../../context/signup'
import { NewStyled as ParentStyled } from '../../styled'
import { useSignupProductOptions } from '../../hooks/useSignupProductOptions'
import { Button } from '../../../../components/UI/Button'
import { useParams, useRegistrationContext } from '../../context/registration'
import { renderRichtext } from '../../../../lib/utils/renderRichtext'
import { Styled } from './styled'
import { Checkbox, ErrorMessage, Select, Textarea, TextInput } from '../../../../components/UI'
import { useRegistrationForm, useRegistrationLinkHandler } from './hooks'
import { useTranslation } from '../../../../lib/context/i18n'
import { useState } from 'react'
import { containsForbiddenDomain } from '../../../ContactPanel'
import emailValidator from 'email-validator';

const splitOptions = (options: string) => {
  if (!options) return []
  return options.split('\n').map(option => option.trim())
}

export const SignupForm = ({ currentOption, loading, isValid, error }) => {
  const { blok, goto } = useRegistrationContext()
  const queryParams = typeof window !== 'undefined' && new URLSearchParams(location?.search);
  const emailParam = typeof window !== 'undefined' && queryParams.get('email');

  const [emailError, setEmailError] = useState('');

  const {
    setStep,
    email,
    setEmail,
    companyName,
    setCompanyName,
    acceptNewsletter,
    setAcceptNewsletter,
    acceptTerms,
    setAcceptTerms,
    businessRole,
    setBusinessRole,
    message,
    setMessage,
    productName,
    productVariant,
    operatingSystem,
  } = useSignupContext()
  const t = useTranslation()
  const options = blok ? splitOptions(blok.businessRoleOptions) : []

  const handleEmailChange = (value) => {
    if (value && (!emailValidator.validate(value) || containsForbiddenDomain(value))) {
      setEmailError(`Please enter your business email address. This form does not accept addresses from ${value.split('@')[1]}.`);
    } else {
      setEmailError('');
    }
  };

  React.useEffect(() => {
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [emailParam])

  return (
    <>
      <ParentStyled.CardHeader>
        <ParentStyled.CardTitle>
          {t('FREE_TRIAL')}:
          &#160;{currentOption?.name}
        </ParentStyled.CardTitle>
        {blok?.registrationFormSubline && (
          <ParentStyled.CardSubline >
            {renderRichtext(blok.registrationFormSubline)}
          </ParentStyled.CardSubline>
        )}
      </ParentStyled.CardHeader>
      <Styled.Wrapper>
        {error && <ErrorMessage $variant="light">{t(error)}</ErrorMessage>}
        {emailError && <ErrorMessage $variant="light">{emailError}</ErrorMessage>}
        <Styled.FieldWrapper>
          <Styled.InvisibleFields>
            <TextInput
              label={'Product Variant'}
              variant="light"
              disabled={true}
              value={productVariant}
            />
            <TextInput
              label={'Product'}
              variant="light"
              disabled={true}
              value={productName}
            />
            <TextInput
              label={'Operating System'}
              variant="light"
              disabled={true}
              // return only last 3 characters of the operating system WIN, LIN, MAC
              value={operatingSystem.slice(-3)}
            />
          </Styled.InvisibleFields>
          <Styled.Fields>
            <TextInput
              label={t('REGISTER_EMAIL')}
              value={email}
              onChange={e => setEmail(e.currentTarget.value)}
              onBlur={e => handleEmailChange(e.currentTarget.value)}
              variant="dark"
              type="email"
              disabled={loading}
            />
            <TextInput
              label={t('REGISTER_COMPANY_NAME')}
              value={companyName}
              onChange={e => setCompanyName(e.currentTarget.value)}
              variant="dark"
              disabled={loading}
            />
            <Select
              label={t('REGISTER_BUSINESS_ROLE')}
              variant="dark"
              value={businessRole}
              onChange={e => setBusinessRole(e.currentTarget.value)}
              disabled={loading}
            >
              {options.map((option, i) => (
                <option key={`${option}_${i}`}>{option}</option>
              ))}
            </Select>
          </Styled.Fields>
          <Styled.Fields>
            <Textarea
              label={t('REGISTER_YOUR_MESSAGE')}
              onChange={e => setMessage(e.currentTarget.value)}
              variant="dark"
              value={message}
              disabled={loading}
            ></Textarea>
          </Styled.Fields>
        </Styled.FieldWrapper>
        <Styled.Fields>
          <Checkbox
            checked={acceptNewsletter}
            label={t('REGISTER_ACCEPT_NEWSLETTER')}
            onChange={e => setAcceptNewsletter(e.currentTarget.checked)}
            variant="dark"
            disabled={loading}
          />
          <Checkbox
            checked={acceptTerms}
            onChange={e => setAcceptTerms(e.currentTarget.checked)}
            disabled={loading}
            label={<>{blok?.acceptTermsText && renderRichtext(blok?.acceptTermsText)}</>}
            variant="dark"
          />
        </Styled.Fields>
      </Styled.Wrapper >
      <ParentStyled.CardFooter>
        <ParentStyled.CardFooterGroup>
          <Button iconRight="spark" $variant="primary" type="submit"
            disabled={!isValid || loading || !!emailError}
            $inverted>
            {t('SIGN_UP_AND_START_FREE')}
          </Button>
          <Button
            iconLeft="refresh"
            $variant="secondary"
            type="button"
            disabled={loading}
            $inverted
            onClick={() => setStep(0)}
          >
            {t('CHANGE_PRODUCT')}
          </Button>
        </ParentStyled.CardFooterGroup>
        <Button
          iconRight="login"
          $variant="tertiary"
          $inverted
          onClick={() => goto('/login')}
          disabled={loading}
        >
          {t('ALREADY_HAVE_EXISTING_ACCOUNT')}
        </Button>
      </ParentStyled.CardFooter>
    </>
  )
}
